import { Link as TonicLink, useTheme, useColorMode } from '@tonic-ui/react';
import { useHistory } from 'react-router-dom';
import React, { useContext, ReactNode, MouseEventHandler } from 'react';
import { AuthContext } from '../context/AuthContext';

interface RouteLinkProps extends StyledSystemProps {
  href: string;
  children?: ReactNode;
  onClick?: MouseEventHandler;
  className?: string;
  id?: string;
  changeApp?: boolean;
  linkColor?: string;
}

const RouteLink = ({
  href,
  id,
  changeApp = false,
  onClick,
  children,
  linkColor,
  ...props
}: RouteLinkProps): JSX.Element => {
  const history = useHistory();
  const colorMode = useColorMode();
  const { eventDistributor: globalEventDistributor } = useContext(AuthContext);

  if (!href) {
    throw new Error('RouteLink missing required props: href or onClick');
  }

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    if (onClick) {
      // Don't allow regular page navigation. Stop and go through react-router or single-spa container
      onClick(event);
    }

    if (changeApp === true) {
      return globalEventDistributor.dispatch({ type: 'NAVIGATE_APP', appName: href.substring(1) });
    } else {
      history.push(href);
    }
  };

  return (
    <TonicLink
      href={href}
      data-id={id}
      data-telemetry-id={id}
      _visited={{
        color: `${linkColor || { light: 'blue:60', dark: 'blue:40' }[colorMode]}`,
      }} // to preserve the design color after the link has been clicked
      textDecoration="underline"
      onClick={handleClick}
      {...(linkColor && { color: linkColor })}
      {...props}
    >
      {children}
    </TonicLink>
  );
};

RouteLink.displayName = 'RouteLink';

export default RouteLink;
